import {observer} from 'mobx-react';
import {Input, FormLayout, Panel, RadioButtonGroup, PanelRadioButton, CheckboxWithLabel, PanelTooltip} from 'shared/components';
import {t} from 'shared/core';
import {useModal} from 'shared/hooks';
import LocationPickerModal from './LocationPickerModal';
import {FormattedMessage} from 'react-intl';

const PolicyDetails = observer(({uiState}) => {
  const {policy, errors} = uiState;
  const locationModalOpen = useModal(uiState, 'LocationPickerModal');

  return (
    <Panel.Stack loose>
      <Panel>
        <Panel.Header
          title={t('time_tracking.manage.policies.edit.Policy Details')}
          description={t('time_tracking.manage.policies.edit.Start by setting up the basic details of your policy.')}
        />
        <FormLayout>
          <Input
            value={policy.name}
            label='time_tracking.manage.policies.edit.Policy Name'
            onChange={e => policy.merge({name: e.target.value})}
            errorMessage={errors.name}
          />
        </FormLayout>
      </Panel>
      <Panel>
        <Panel.Header
          title={t('time_tracking.manage.policies.edit.policy_details.Time Entry Configuration')}
          description={t('time_tracking.manage.policies.edit.policy_details.Configure how employees will enter time into their timesheets.')}
        />
        <RadioButtonGroup
          className='table'
          name='overtimePolicy'
          selectedValue={policy.manuallyEnterHours}
          onChange={(_, value) => policy.setManuallyEnteredHours(value)}
        >
          <PanelRadioButton
            value={false}
            label='time_tracking.manage.policies.edit.policy_details.Start and End Times'
            description='time_tracking.manage.policies.edit.policy_details.Employees enter their start and end times for a given day.'
          >
            <div>
              <CheckboxWithLabel
                label='time_tracking.manage.policies.edit.policy_details.Enable Clocking In and Out'
                value={policy.timeClockEnabled}
                checked={policy.timeClockEnabled}
                onChange={e => policy.setTimeClockEnabled(e.checked)}/>
            </div>
            <div>
              <CheckboxWithLabel
                label="time_tracking.manage.policies.edit.policy_details.Collect Employee's Clock In and Out Location"
                value={policy.collectEmployeeLocation}
                checked={policy.collectEmployeeLocation}
                onChange={e => policy.setCollectEmployeeLocation(e.checked)}
                disabled={!policy.timeClockEnabled}
              />
            </div>
            <div>
              <CheckboxWithLabel
                label='time_tracking.manage.policies.edit.policy_details.Enable Work Location'
                value={!!policy.timeClockLocation}
                checked={!!policy.timeClockLocation}
                onChange={e => e.checked ? uiState.openLocationPickerModal() : policy.setTimeClockLocation(null)}
                disabled={!policy.collectEmployeeLocation}
              />
              {policy.timeClockLocation && <div style={{marginLeft: '24px'}} className='jumbo mtn2 flex align-items-middle gap-1 mb1'>
                <span>{policy.timeClockLocation.address}</span>
                <i onClick={() => uiState.openLocationPickerModal()} className='h3 material-symbols-solid waterloo clickable'>{'edit'}</i>
              </div>}
            </div>
            <div className='flex align-items-center'>
              <CheckboxWithLabel
                label='time_tracking.manage.policies.edit.policy_details.Enable Geofencing'
                value={policy.geofencingEnabled}
                checked={policy.geofencingEnabled}
                onChange={e => policy.merge({geofencingEnabled: e.checked})}
                disabled={!policy.collectEmployeeLocation || !policy.timeClockLocation}
              />
              <div className='mb2'>
                <PanelTooltip>
                  <div className='mb2'><FormattedMessage id='time_tracking.manage.policies.edit.policy_details.Enabling this option will require employees to clock in and out within the work location.'/></div>
                  <div><FormattedMessage id='time_tracking.manage.policies.edit.policy_details.Employees will only be able to add entries to their timesheets by clocking in or out.'/></div>
                </PanelTooltip>
              </div>
            </div>
            <LocationPickerModal
              policy={policy}
              onSave={(coords) => {
                policy.setTimeClockLocation(
                  coords ? {latitude: coords.lat, longitude: coords.lng, address: coords.address} : null
                );
                uiState.closeLocationPickerModal();
              }}
              onClose={() => uiState.closeLocationPickerModal()}
              modalOpen={locationModalOpen}
            />
          </PanelRadioButton>
          <PanelRadioButton
            value={true}
            label='time_tracking.manage.policies.edit.policy_details.Total Hours'
            description='time_tracking.manage.policies.edit.policy_details.Employees enter the total hours worked for a given day.'
          >
          </PanelRadioButton>
        </RadioButtonGroup>
      </Panel>
    </Panel.Stack>
  );
});

export default PolicyDetails;
